// =============================================
// GIC RATES PAGE DATA — FinMaple
// Updated monthly. Change rates + lastUpdated below.
// =============================================

/**
 * MONTHLY UPDATE CHECKLIST:
 * 1. Update `lastUpdated` to today's date
 * 2. Update `currentMonth` and `currentMonthYear` strings
 * 3. Update rate numbers in `rates` (by term)
 * 4. Update `big5Comparison` rates
 * 5. Update `atomicAnswer` text if best rates changed
 * 6. Update `seo.title` / `seo.description` month reference
 * 7. Update `schema.dateModified`
 * 8. Rebuild: npm run build
 */

// ── Meta ──────────────────────────────────────
export const GIC_META = {
  lastUpdated: '2026-03-08',           // YYYY-MM-DD — change each update
  currentMonth: 'marzo',               // Spanish month name (lowercase)
  currentMonthYear: 'marzo 2026',      // "month YYYY" for display strings
  ratesAsOf: '2026-02-25',             // Date rates were sourced
  bocOvernightRate: 2.25,              // Bank of Canada overnight rate
};

// ── SEO & Schema ──────────────────────────────
export const GIC_SEO = {
  title: `Mejores Tasas GIC en Canadá - Marzo 2026`,
  description: 'Compara tasas de GIC por plazo: 1, 2, 3, 4 y 5 años (hasta 3.80%). Bancos online ofrecen ~1% más que Big 5.',
  canonical: '/tasas-gic',
  schema: {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: 'Mejores Tasas GIC en Canadá - FinMaple',
    description: 'Compara tasas de GIC (Guaranteed Investment Certificate) por plazo: 1, 2, 3, 4 y 5 años. Bancos online vs Big 5.',
    provider: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    dateModified: '2026-03-08',         // Update each time rates change
  },
};

// ── Page Header ───────────────────────────────
export const GIC_PAGE_HEADER = {
  title: 'Mejores tasas GIC en Canadá - comparadas para marzo 2026',
  subtitle: 'Tasas de Guaranteed Investment Certificate por plazo. Bancos online vs Big 5.',
  breadcrumbs: [{ label: 'Tasas GIC' }],
};

// ── Atomic Answer (summary strip) ─────────────
export const GIC_ATOMIC_ANSWER =
  'Los bancos online (WealthONE, Achieva, Oaken) ofrecen tasas GIC ~1% más altas que los Big 5. ' +
  'La mejor tasa a 1 año es 3.65% (WealthONE) y a 5 años es 3.80% (Achieva/WealthONE/MCAN/Oaken) ' +
  'vs 2.25-2.60% en Big 5. Consejo: compra GICs dentro de un TFSA para que el interés sea 100% libre de impuestos.';

// ── Cross-links ───────────────────────────────
export const GIC_CROSS_LINKS = [
  { label: 'Cuentas registradas (TFSA, RRSP, FHSA)', to: '/cuentas-registradas' },
  { label: 'Comparar cuentas bancarias', to: '/comparar-cuentas' },
  { label: 'Calculadora de hipoteca', to: '/calculadora-hipoteca' },
];

// ── FAQs ──────────────────────────────────────
export const GIC_FAQS = [
  {
    question: '¿Qué es un GIC?',
    answer:
      'Un GIC (Guaranteed Investment Certificate) es un depósito a plazo fijo donde el banco te garantiza una tasa de interés por un período determinado (1-5 años). Tu dinero está protegido por CDIC hasta $100,000. Es una inversión de bajo riesgo ideal para emergency fund o down payment.',
  },
  {
    question: '¿Puedo comprar un GIC como newcomer?',
    answer:
      'Sí. Necesitas SIN, ID del gobierno, dirección canadiense y ser mayor de edad. Los estudiantes internacionales en el programa SDS necesitan un GIC de $20,635 como parte del proceso de visa.',
  },
  {
    question: '¿Por qué los bancos online ofrecen mejores tasas?',
    answer:
      'Los bancos online (EQ Bank, Oaken, WealthONE) no tienen gastos de sucursales físicas, lo que les permite ofrecer tasas ~1% más altas que los Big 5. Un GIC de $10,000 a 1 año en un banco online te da ~$100 más de interés que en un Big 5.',
  },
  {
    question: '¿GIC o TFSA para ahorrar?',
    answer:
      'Idealmente ambos: abre un TFSA y compra GICs dentro del TFSA. Así obtienes la tasa garantizada del GIC + el beneficio fiscal del TFSA (interés libre de impuestos). EQ Bank y Oaken Financial ofrecen GICs dentro de TFSA.',
  },
];
